import { AnimatePresence, motion } from 'framer-motion';
import { Check, Eye, Loader2, MoreVertical, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Kbd } from '@/components/ui-kit/kbd';
import { useBuilder } from './typeform-builder';

interface BuilderHeaderProps {
  lastSaved: Date | null;
  publishing: boolean;
  hasUnpublished: boolean;
  onPublishUilm: () => void;
  onPreviewClick: () => void;
  /** Optional override for the back-arrow click. When omitted (e.g. on
      desktop or at the mobile L1 root), falls back to `navigate(-1)` to
      exit the builder. Mobile L2/L3 pass a layer-up handler. */
  onBack?: () => void;
  /** Optional override for the title shown next to the back arrow. Used
      by the mobile wizard to surface the current screen/field name. */
  title?: string;
}

export function BuilderHeader({
  lastSaved,
  publishing,
  hasUnpublished,
  onPublishUilm,
  onPreviewClick,
  onBack,
  title,
}: BuilderHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { template, saving } = useBuilder();
  const handleBack = onBack ?? (() => navigate(-1));
  const displayTitle = title ?? template.title;

  return (
    <header className="h-14 border-b border-border bg-[hsl(var(--bg))] flex items-center justify-between gap-2 px-2 md:px-4 shrink-0">
      <div className="flex items-center gap-2 md:gap-4 min-w-0">
        <button
          onClick={handleBack}
          aria-label="Back"
          className="shrink-0 p-2 rounded-lg text-globe-grey hover:text-[hsl(var(--fg))] hover:bg-muted transition-colors"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <span className="font-semibold text-[hsl(var(--fg))] truncate">{displayTitle}</span>
          <AnimatePresence mode="wait">
            {saving ? (
              <motion.div
                key="saving"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted text-xs text-globe-grey"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="h-3 w-3 border border-globe-grey border-t-transparent rounded-full"
                />
                {t('TEMPLATEBUILDER_BUILDER_SAVING')}
              </motion.div>
            ) : lastSaved ? (
              <motion.div
                key="saved"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-lime-green/10 text-xs text-poly-green"
              >
                <Check className="h-3 w-3" />
                {t('TEMPLATEBUILDER_BUILDER_SAVED')}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
      <div className="flex items-center gap-1 md:gap-2 shrink-0">
        {/* Keyboard shortcuts hint */}
        <div className="hidden lg:flex items-center gap-3 mr-4 text-xs text-globe-grey">
          <span className="flex items-center gap-1">
            <Kbd>&#8593;&#8595;</Kbd>
            {t('TEMPLATEBUILDER_BUILDER_NAVIGATE')}
          </span>
        </div>

        {/* Desktop: inline Publish + Preview buttons */}
        <div className="hidden md:flex items-center gap-2">
          {/* Publish UILM keys — system templates only. Republishes the
              cached UILM file so saved-but-unpublished keys become live.
              Amber dot when there are unpublished edits since last publish. */}
          {!template.organizationId && (
            <Button
              onClick={onPublishUilm}
              disabled={publishing}
              variant="primary"
              size="sm"
              title="Republish the UILM file so this template's saved keys become live"
              aria-label="Publish UILM keys"
            >
              {publishing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              <span>{publishing ? 'Publishing…' : 'Publish UILM keys'}</span>
              {hasUnpublished && !publishing && (
                <span
                  className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-[hsl(var(--bg))]"
                  aria-label="Unpublished changes"
                />
              )}
            </Button>
          )}
          <Button
            variant="primaryOutline"
            size="sm"
            onClick={onPreviewClick}
            aria-label={t('TEMPLATEBUILDER_BUILDER_PREVIEW')}
          >
            <Eye className="h-4 w-4" />
            <span>{t('TEMPLATEBUILDER_BUILDER_PREVIEW')}</span>
          </Button>
        </div>

        {/* Mobile: same actions collapsed into a three-dot overflow menu.
            The amber unpublished dot bubbles up to the trigger so the
            indicator is still visible without opening the menu. */}
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                className="relative shrink-0 p-2 rounded-lg text-globe-grey hover:text-[hsl(var(--fg))] hover:bg-muted transition-colors"
              >
                <MoreVertical className="h-5 w-5" />
                {!template.organizationId && hasUnpublished && !publishing && (
                  <span
                    className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-[hsl(var(--bg))]"
                    aria-label="Unpublished changes"
                  />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={onPreviewClick}>
                <Eye className="h-4 w-4" />
                <span>{t('TEMPLATEBUILDER_BUILDER_PREVIEW')}</span>
              </DropdownMenuItem>
              {!template.organizationId && (
                <DropdownMenuItem onClick={onPublishUilm} disabled={publishing} className="gap-2">
                  {publishing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  <span>{publishing ? 'Publishing…' : 'Publish UILM keys'}</span>
                  {hasUnpublished && !publishing && (
                    <span className="ml-auto h-2 w-2 rounded-full bg-amber-500" aria-hidden />
                  )}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
